import { useEffect, useRef, useState } from 'react';

interface Contributor {
  name: string;
  location: string;
  region: string;
  role: string;
  coordinates: [number, number]; // [lng, lat]
}

const contributors: Contributor[] = [
  {
    name: 'Ben',
    location: 'Jericho, Colombia',
    region: 'South America',
    role: 'Founding Member, Core Contributor',
    coordinates: [-73.06, 7.07],
  },
  {
    name: 'Engie',
    location: 'Northland (Te Tai Tokerau), New Zealand',
    region: 'Aotearoa',
    role: 'Founding Member, Core Contributor',
    coordinates: [174.32, -35.73],
  },
  {
    name: 'Cherese',
    location: 'Waikato, New Zealand',
    region: 'Aotearoa',
    role: 'Founding Member, Core Contributor',
    coordinates: [175.28, -37.78],
  },
  {
    name: 'Tyrone',
    location: 'Kaitaia, Northland (Te Tai Tokerau), New Zealand',
    region: 'Aotearoa',
    role: 'Contributor',
    coordinates: [173.26, -35.11],
  },
  {
    name: 'Ian',
    location: 'Mauke, Cook Islands',
    region: 'Pacific Islands',
    role: 'Contributor',
    coordinates: [-157.35, -20.15],
  },
  {
    name: 'Gabriel',
    location: 'El Paso, Texas, USA',
    region: 'North America',
    role: 'Contributor',
    coordinates: [-106.44, 31.76],
  },
];

export default function Contributors() {
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [selectedContributor, setSelectedContributor] = useState<Contributor | null>(null);
  const [mapError, setMapError] = useState(false);

  useEffect(() => {
    if (!mapContainer.current || mapRef.current) return;

    const token = import.meta.env.VITE_MAPBOX_TOKEN;
    if (!token) {
      setMapError(true);
      return;
    }

    let map: mapboxgl.Map;

    const initMap = async () => {
      const mapboxgl = (await import('mapbox-gl')).default;
      mapboxgl.accessToken = token;

      map = new mapboxgl.Map({
        container: mapContainer.current!,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [174, -41],
        zoom: 1.3,
        projection: 'globe',
        attributionControl: false,
        scrollZoom: false,
        boxZoom: false,
        doubleClickZoom: false,
        touchZoomRotate: false,
      });

      map.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-right');

      map.on('style.load', () => {
        map.setFog({
          color: '#F5F5F0',
          'high-color': '#619793',
          'horizon-blend': 0.04,
          'space-color': '#253D39',
          'star-intensity': 0.3,
        });
      });

      map.on('load', () => {
        contributors.forEach((contributor) => {
          // Create marker element — outer wrapper must not have transform
          // since Mapbox uses transform: translate() to position markers
          const el = document.createElement('div');
          el.className = 'matou-marker';
          el.style.cssText = `
            cursor: pointer;
          `;

          const dot = document.createElement('div');
          dot.style.cssText = `
            width: 14px;
            height: 14px;
            background: #619793;
            border: 2px solid #253D39;
            border-radius: 50%;
            box-shadow: 0 0 8px rgba(97, 151, 147, 0.5);
            transition: transform 150ms ease, box-shadow 150ms ease;
          `;
          el.appendChild(dot);

          el.addEventListener('mouseenter', () => {
            dot.style.transform = 'scale(1.5)';
            dot.style.boxShadow = '0 0 16px rgba(97, 151, 147, 0.8)';
          });
          el.addEventListener('mouseleave', () => {
            dot.style.transform = 'scale(1)';
            dot.style.boxShadow = '0 0 8px rgba(97, 151, 147, 0.5)';
          });

          // Popup for hover
          const popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 15,
            className: 'matou-popup',
          }).setHTML(`
            <div style="font-family: 'Roboto Mono', monospace; font-size: 13px;">
              <strong style="color: #253D39;">${contributor.name}</strong>
              <div style="color: #666666; font-size: 11px; margin-top: 2px;">${contributor.region}</div>
            </div>
          `);

          const marker = new mapboxgl.Marker(el)
            .setLngLat(contributor.coordinates)
            .addTo(map);

          el.addEventListener('mouseenter', () => {
            popup.setLngLat(contributor.coordinates).addTo(map);
          });
          el.addEventListener('mouseleave', () => {
            popup.remove();
          });
          el.addEventListener('click', () => {
            setSelectedContributor(contributor);
            map.flyTo({
              center: contributor.coordinates,
              zoom: 4,
              duration: 1500,
            });
          });
        });
      });

      mapRef.current = map;
    };

    initMap();

    return () => {
      if (map) map.remove();
      mapRef.current = null;
    };
  }, []);

  return (
    <section id="contributors" className="py-16 md:py-24 bg-matou-teal-dark">
      <div className="max-w-content mx-auto px-4 md:px-8">
        {/* Section Title */}
        <div className="max-w-4xl mx-auto text-center mb-12">
          <h2 className="font-heading text-white text-2xl md:text-3xl font-bold mb-4">
            Who's Coming With Us
          </h2>
          <p className="font-mono text-matou-teal-soft text-sm md:text-base leading-relaxed">
            Our membership is growing and we hope to have contributors from all different cultures and regions of our planet, united by a shared commitment to indigenous autonomy and digital sovereignty.
          </p>
        </div>

        {/* Map Header */}
        <div className="bg-matou-teal-deep rounded-t-md px-5 py-3">
          <h2 className="font-heading text-matou-teal-soft text-sm font-bold uppercase tracking-widest">
            Our Contributors
          </h2>
        </div>

        {/* Map Container */}
        <div className="border border-t-0 border-black/10 rounded-b-md overflow-hidden">
          {mapError ? (
            // Fallback when no Mapbox token
            <div className="bg-matou-bg" style={{ height: '500px' }}>
              <div className="h-full flex flex-col items-center justify-center px-4">
                <pre className="text-matou-teal-medium text-xs mb-6 text-center font-mono select-none" aria-hidden="true">{`
        ╭───────────────────────────────────╮
       ╱    ·  ·        ·    ·    ·   ·     ╲
      │  ·    ★     ·       ★    ·    ★   ·  │
      │    ·    ·  ·   ★  ·    ·    ·    ·   │
       ╲  ·   ·    ·  ·    ·  ★  ·    ·    ╱
        ╰───────────────────────────────────╯
                `}</pre>
                <p className="font-mono text-sm text-matou-text-muted text-center mb-6">
                  Interactive map loading...
                </p>
                <p className="font-mono text-xs text-matou-text-muted/60 text-center">
                  Set <code className="bg-matou-bg px-1.5 py-0.5 rounded text-matou-teal-medium">VITE_MAPBOX_TOKEN</code> in your environment to enable the map.
                </p>

                {/* Fallback contributor list */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mt-8 w-full max-w-3xl">
                  {contributors.map((c) => (
                    <div
                      key={c.name + c.location}
                      className="bg-matou-surface border border-black/10 rounded-lg p-4 hover:-translate-y-0.5 transition-transform duration-150"
                    >
                      <div className="w-3 h-3 rounded-full bg-matou-teal-medium mb-3" />
                      <p className="font-mono text-sm font-semibold text-matou-text">{c.name}</p>
                      <p className="font-mono text-xs text-matou-text-muted mt-1">{c.location}</p>
                      <p className="font-mono text-xs text-matou-teal-medium mt-1">{c.role}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="relative">
              <div ref={mapContainer} style={{ height: '500px', width: '100%' }} />

              {/* Selected contributor detail */}
              {selectedContributor && (
                <div className="absolute bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 bg-matou-surface border-t-4 border-matou-teal-medium rounded-lg shadow-xl p-5 animate-slide-up">
                  <button
                    onClick={() => setSelectedContributor(null)}
                    className="absolute top-3 right-3 text-matou-text-muted hover:text-matou-text transition-colors"
                    aria-label="Close"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-4 h-4 rounded-full bg-matou-teal-medium border-2 border-matou-teal-dark" />
                    <h3 className="font-heading text-lg font-bold text-matou-text">
                      {selectedContributor.name}
                    </h3>
                  </div>
                  <div className="space-y-2">
                    <p className="font-mono text-sm text-matou-text-muted">
                      {selectedContributor.location}
                    </p>
                    <span className="inline-block font-mono text-xs font-semibold uppercase tracking-wider bg-matou-teal-soft/30 text-matou-teal-dark px-3 py-1 rounded">
                      {selectedContributor.role}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 300ms ease forwards;
        }
      `}</style>
    </section>
  );
}
